import * as React from 'react';
import _ from 'lodash';
import clsx from 'clsx';

import { Party } from 'types/service';
import { getIconForShape } from 'constants/AerialConstants';
import { getShouldEnablePartyNotifyIndicator } from 'utils/partyUtils';
import { ifClickable } from '@tock/shared/utils/touchUtils';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import protos from 'utils/protos';

const { TableShape } = protos;

export interface Props extends Table {
  backgroundClassName?: string;
  foregroundClassName?: string;
  hasError?: boolean;
  isSelectedParty: boolean;
  id: number;
  isDragPreview?: boolean;
  isSelectedTable: boolean;
  modifierClassName?: string;
  onTableClicked(tappedTableId: number): void;
  serverColorClassName?: string | null;
  sizeOverride?: number | string;
  party?: Party;
}

class AerialTable extends PureBaseComponent<Props> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStyleForRotation(rotation: any) {
    if (rotation === 0) {
      return {};
    } else {
      return {
        transform: `rotate(${rotation}deg)`,
      };
    }
  }

  handleClick(event: React.PointerEvent<HTMLElement>) {
    preventBubbleOutside(event);
    this.props.onTableClicked(this.props.id);
  }

  render() {
    const {
      location: propLocation,
      modifierClassName,
      isSelectedTable,
      isSelectedParty,
      hasError,
      isDragPreview,
      foregroundClassName,
      serverColorClassName,
      party,
      minPartySize,
      backgroundClassName,
      size: propSize,
      sizeOverride,
      name,
    } = this.props;
    const location = propLocation || {
      rotation: 0,
      shape: TableShape.SQUARE,
      visualSize: 2,
    }; // Defaults
    const shouldEnablePartyNotifyIndicator = getShouldEnablePartyNotifyIndicator(party);

    // which corner to put the notification on
    // 0: top left
    // 1: bottom left
    // 2: bottom right
    // 3: top right
    const cornerCase = Math.floor((location.rotation % 360) / 90);

    const className = clsx(
      'AerialTable',
      `AerialTable--visualSize${location.visualSize}`,
      modifierClassName,
      {
        'has-error': hasError,
        'is-selected': isSelectedTable || isSelectedParty,
        'is-preview': isDragPreview,
      }
    );
    const detailsClassName = clsx('AerialTable-tableDetails', foregroundClassName);
    const dividerClassName = clsx('AerialTable-tableDetailsDivider', foregroundClassName);
    const serverBorderClassName = clsx(
      'AerialTable-serverBorder',
      serverColorClassName,
      getIconForShape(location.shape),
      {
        'ServiceAerialTable-notify': shouldEnablePartyNotifyIndicator,
        'notify-top-left': cornerCase === 0,
        'notify-bottom-left': cornerCase === 1,
        'notify-bottom-right': cornerCase === 2,
        'notify-top-right': cornerCase === 3,
      }
    );
    const tableShapeClassName = clsx(
      'AerialTable-tableShape',
      backgroundClassName,
      getIconForShape(location.shape),
      {
        'has-no-server': _.isEmpty(serverColorClassName),
      }
    );

    let size: string | number = propSize;
    if (minPartySize && size !== minPartySize) {
      size = `${minPartySize}-${size}`;
    }
    if (sizeOverride) {
      size = sizeOverride;
    }

    return (
      <div className={className} onClick={ifClickable(this.handleClick)} data-testid="table-aerial">
        <div className={tableShapeClassName} style={this.getStyleForRotation(location.rotation)} />
        <div
          className={serverBorderClassName}
          style={this.getStyleForRotation(location.rotation)}
        />
        <div className={detailsClassName}>
          <div className="AerialTable-tableName" data-testid={'table-' + name}>
            {name}
          </div>
          <div className={dividerClassName} />
          <div className="AerialTable-tableSize">{size}</div>
        </div>
      </div>
    );
  }
}

export default AerialTable;
